import { useCallback, useEffect, useState } from 'react';

/** Desktop Orders tab interval. The getOrders cache is shorter, so each tick is a new read and the positions call in the same tick is served from that read. */
export const BLOTTER_REFRESH_MS = 30_000;

/**
 * Ticks while the blotter is open, refresh is not paused, and the tab is visible.
 * The order list and the positions list share one tick.
 */
export function useBlotterRefresh(enabled: boolean): { tick: number; paused: boolean; held: boolean; togglePaused: () => void } {
  const [tick, setTick] = useState(0);
  const [paused, setPaused] = useState(false);
  const [hidden, setHidden] = useState(() => typeof document !== 'undefined' && document.visibilityState === 'hidden');

  useEffect(() => {
    const onVisibility = () => {
      const isHidden = document.visibilityState === 'hidden';
      setHidden(isHidden);
      if (!isHidden && enabled && !paused) setTick((current) => current + 1);
    };
    document.addEventListener('visibilitychange', onVisibility);
    return () => document.removeEventListener('visibilitychange', onVisibility);
  }, [enabled, paused]);

  useEffect(() => {
    if (!enabled || paused) return;
    const id = window.setInterval(() => {
      if (document.visibilityState === 'hidden') return;
      setTick((current) => current + 1);
    }, BLOTTER_REFRESH_MS);
    return () => window.clearInterval(id);
  }, [enabled, paused]);

  const togglePaused = useCallback(() => setPaused((current) => !current), []);

  return { tick, paused, held: paused || hidden, togglePaused };
}
